import _ from "lodash";

import { BenchmarkThreshold } from "@/utils/types";
import { simpleFormatNumberEquity } from "lib/frontend/cm/utils/helpers";
import { Company, Range, SalaryIncomeFrequency, User } from "prisma/cm/client";

import { annualizeCashValue, decimalsForRangeNominal } from "../compensation";
import { calculateOTEFromUser } from "../compensation/calculateOTEFromUser";
import { EMPLOYMENT_SUBTYPE_FULL_TIME } from "../compensation/employment/constants";
import { getConvertedCurrency } from "../currency/getConvertedCurrency";
import { getCurrencyOffInstance } from "../currency/getCurrencyField";
import { annualizeAndConvertValue } from "../proposals";
import { ReviewExchangeRates } from "../review/types";

export function getSalaryBandRange(
  range: Range,
  company: Company,
  {
    exchangeRates,
    targetCurrency,
    isOTE = false,
  }: {
    exchangeRates: ReviewExchangeRates;
    targetCurrency: string;
    isOTE?: boolean;
  },
) {
  if (!range) {
    return "";
  }

  const decimals = decimalsForRangeNominal({ range, company });

  // @ts-expect-error TS2339
  const salaryLow = isOTE ? range.totalPayLow : range.salaryLow;
  // @ts-expect-error TS2339
  const salaryHigh = isOTE ? range.totalPayHigh : range.salaryHigh;

  if (!salaryLow || !salaryHigh) {
    return "";
  }

  const formattedSalaryLow = getConvertedCurrency({
    value: salaryLow,
    options: {
      sourceCurrency: range.salaryCurrencyCode,
      targetCurrency: targetCurrency,
      exchangeRates,
      decimals,
    },
  }).formatted;

  const formattedSalaryHigh = getConvertedCurrency({
    value: salaryHigh,
    options: {
      sourceCurrency: range.salaryCurrencyCode,
      targetCurrency: targetCurrency,
      exchangeRates,
      decimals,
    },
  }).formatted;

  return `${formattedSalaryLow} - ${formattedSalaryHigh}`;
}

export function getEquityBandRange(range: Range) {
  if (!range) {
    return "";
  }

  // @ts-expect-error TS2339
  const equityLow = range.hireGrantLow;
  // @ts-expect-error TS2339
  const equityHigh = range.hireGrantHigh;

  if (!equityLow || !equityHigh) {
    return "";
  }

  return `${simpleFormatNumberEquity(equityLow)} - ${simpleFormatNumberEquity(equityHigh)}`;
}

export function userIsOutOfRange(
  company: Company,
  user: User,
  range: Range,
  opts: {
    exchangeRates: ReviewExchangeRates;
    checkOTE?: "withBonusTarget" | "withoutBonusTarget";
  },
) {
  if (!range) {
    return false;
  }

  const rangeCurrency = getCurrencyOffInstance(range);
  const userCurrency = getCurrencyOffInstance(user);

  if (opts.checkOTE) {
    const oteIsAlreadyAnnualized = calculateOTEFromUser(
      user,
      company,
      opts.checkOTE === "withBonusTarget",
    );

    const oteInRangeCurrency = getConvertedCurrency({
      value: oteIsAlreadyAnnualized,
      options: {
        sourceCurrency: userCurrency,
        targetCurrency: rangeCurrency,
        exchangeRates: opts.exchangeRates,
      },
    }).raw;

    const oteMin = annualizeCashValue({
      // @ts-expect-error TS2339
      value: range.totalPayLow,
      incomeFrequency: range.salaryIncomeFrequency,
    });

    const oteMax = annualizeCashValue({
      // @ts-expect-error TS2339
      value: range.totalPayHigh,
      incomeFrequency: range.salaryIncomeFrequency,
    });

    if (!oteMin || !oteMax) {
      return false;
    }

    return oteInRangeCurrency < oteMin || oteInRangeCurrency > oteMax;
  }

  const userSalaryInRangeCurrency = annualizeAndConvertValue({
    value: user.salary ?? 0,
    sourceCurrency: userCurrency,
    targetCurrency: rangeCurrency,
    exchangeRates: opts.exchangeRates,
    incomeFrequency: user.salaryIncomeFrequency ?? SalaryIncomeFrequency.YEARLY,
    employmentSubType: user.employmentSubType ?? EMPLOYMENT_SUBTYPE_FULL_TIME,
  }).raw;

  const salaryMin = annualizeCashValue({
    // @ts-expect-error TS2339
    value: range.salaryLow,
    incomeFrequency: range.salaryIncomeFrequency,
  });

  // @ts-expect-error TS2339
  const salaryMax = range.salaryHigh;

  if (!salaryMin || !salaryMax) {
    return false;
  }

  return userSalaryInRangeCurrency < salaryMin || userSalaryInRangeCurrency > salaryMax;
}

export function getRangeSalaryLow(range: Range) {
  // @ts-expect-error TS2339
  return range.salaryLow as number | null;
}

export function getRangeSalaryHigh(range: Range) {
  // @ts-expect-error TS2339
  return range.salaryHigh as number | null;
}

export function getRangeTotalPayLow(range: Range) {
  // @ts-expect-error TS2339
  return range.totalPayLow as number | null;
}

export function getRangeTotalPayHigh(range: Range) {
  // @ts-expect-error TS2339
  return range.totalPayHigh as number | null;
}

/**
 * Moved from legacy frontend/cm/utils/helpers.ts to avoid duplicate definitions.
 * Gets the benchmark value for a given field and threshold.
 *
 * Note: The options parameter (convertCents and forceHistoricalBenchmark) are preserved
 * from the legacy implementation, though their necessity should be re-evaluated.
 */
export const getRangePointValueFromProposal = ({
  proposal,
  field,
  target,
  options,
}: {
  proposal: unknown;
  field: "salary" | "totalPay" | "equity";
  target: BenchmarkThreshold;
  options?: {
    forceHistoricalBenchmark?: boolean;
  };
}): number => {
  const forceHistoricalBenchmark = options?.forceHistoricalBenchmark ?? false;

  const fieldKey = field === "equity" ? "hireGrant" : field;

  const mappings: Record<BenchmarkThreshold, string> = {
    MIN: "Low",
    MID: "Mid",
    MAX: "High",
  };

  const thresholdKey = mappings[target];
  const rangeKey = `${fieldKey}${thresholdKey}`;

  let proposalValue: number = _.get(proposal, ["range", rangeKey], 0);
  let userExistingValue: number = _.get(proposal, ["owner", "range", rangeKey], 0);

  if (forceHistoricalBenchmark) {
    return userExistingValue;
  }

  return _.get(proposal, "range") ? proposalValue : userExistingValue;
};
